// ─────────────────────────────────────────────────────────────
// SettingsTab — appearance + password change.
// Centered content, no sidebars.
// ─────────────────────────────────────────────────────────────

import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import type { PaletteMode } from "@mui/material";

interface SettingsTabProps {
  colorMode: PaletteMode;
  onToggleColorMode: () => void;
}

export function SettingsTab({ colorMode, onToggleColorMode }: SettingsTabProps) {
  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        display: "flex",
        justifyContent: "center",
        px: 3,
        py: 4,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 560 }}>
        {/* ── Appearance ──────────────────────────────────────── */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h3" gutterBottom>Appearance</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {colorMode === "dark" ? (
                  <DarkModeOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
                ) : (
                  <LightModeOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
                )}
                <Box>
                  <Typography variant="subtitle2">
                    {colorMode === "dark" ? "Dark Mode" : "Light Mode"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Choose the interface colour scheme
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={colorMode === "dark"}
                onChange={onToggleColorMode}
                size="small"
                inputProps={{ "aria-label": "Toggle dark mode" }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* ── Change Password ──────────────────────────────────── */}
        <Card>
          <CardContent>
            <Typography variant="h3" gutterBottom>Change Password</Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5}>
              <TextField
                label="Current password"
                type="password"
                fullWidth
                autoComplete="current-password"
              />
              <TextField
                label="New password"
                type="password"
                fullWidth
                autoComplete="new-password"
              />
              <TextField
                label="Confirm new password"
                type="password"
                fullWidth
                autoComplete="new-password"
              />
              <Button variant="contained" sx={{ alignSelf: "flex-start" }}>
                Update Password
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
